import { useEffect, useMemo, useState } from 'react';
import { t } from '@hana/i18n';
import '../strings.i18n';
import { Link } from '@tanstack/react-router';
import {
  IconArrowLeft, IconCheck, IconCoin, IconCopy, IconExternalLink, IconFileText, IconHistory, IconLock, IconPencil, IconPlus, IconPlugConnected, IconRefresh,
  IconSend, IconSpeakerphone, IconMessageCircle, IconUsers, IconAlertTriangle, IconRosetteDiscountCheck, IconX,
} from '@tabler/icons-react';
import { Button, Chip, Input, Modal, Segmented, Select, StateView, Textarea, useToast, type ScreenState } from '@hana/ui';
import { Page, PageHeader } from '@hana/shell';
import {
  BC_USED, DAYS, DAY_MAX, ERRS, EVS, FOLLOWERS, HOOKS, LOG, MEDIAN_SEC, PARAMS, SLA_PIC, TOKENS, TPLS, ZSLA, fmt, mmss, pct,
  type LogStatus, type LogType, type ParamKey, type Tpl, type TplStatus,
} from './zalo-data';
import s from './zalo.module.css';

// Mint-ZaloOA (1536) + Mint-ZaloOA-1920: màn cấu hình tích hợp Zalo OA · mẫu ZNS · nhật ký gửi · thống kê (Quản trị › Tích hợp). Dữ liệu mẫu trong bộ nhớ,
// không gọi Zalo API; "Gửi thử" / "Gửi lại" chỉ báo toast. Chi phí ZNS chỉ ADMIN / BOD, số tiền chỉ khi xem tiền (`?money=view`).

type Tab = 'tpl' | 'log' | 'stat';
// Nội dung mẫu ZNS = dữ liệu (biến <…> theo Zalo), không dịch.
const ph = (k: ParamKey) => `<${k}>`;
const BODY_DELIVERED = `HANA Logistics thông báo lô ${ph('job_id')} đã giao lúc ${ph('delivered_at')}.`;
const BODY_DEBIT = `Kính gửi ${ph('customer')}: debit ${ph('debit_no')} đến hạn ${ph('due_date')}, số tiền ${ph('amount')}.`;

const ST: Record<TplStatus, { k: string; tone: 'ok' | 'warn' | 'err'; dot: string }> = {
  ok: { k: 'Đã duyệt', tone: 'ok', dot: 'var(--ok)' }, wait: { k: 'Chờ duyệt', tone: 'warn', dot: 'var(--warn)' }, rej: { k: 'Bị từ chối', tone: 'err', dot: 'var(--err)' },
};
const TY: Record<LogType, { k: string; tip: string; icon: typeof IconSend; color: string }> = {
  chat: { k: 'Tư vấn', tip: 'Tin tư vấn · trong 48h, khách đã follow OA', icon: IconMessageCircle, color: 'var(--link)' },
  zns: { k: 'ZNS', tip: 'Tin ZNS theo mẫu đã duyệt · gửi theo SĐT', icon: IconFileText, color: 'var(--tx)' },
  bc: { k: 'Broadcast', tip: 'Broadcast cho follower · tối đa 40 tin / 30 ngày', icon: IconSpeakerphone, color: 'var(--warn)' },
};
const SS: Record<LogStatus, { k: string; dot: string; tip: string }> = {
  sent: { k: 'Đã gửi', dot: 'var(--d-blue)', tip: 'Zalo đã nhận lệnh gửi' }, rcv: { k: 'Đã nhận', dot: 'var(--d-green)', tip: 'Đã tới máy người nhận' },
  seen: { k: 'Đã xem', dot: 'var(--ok)', tip: 'Người nhận đã mở tin' }, err: { k: 'Lỗi', dot: 'var(--err)', tip: '' },
};

function readQuery() {
  const q = new URLSearchParams(location.search);
  const v = q.get('state');
  const viewer = q.get('viewer');
  return {
    state: (v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready') as ScreenState,
    money: q.get('money') === 'view',
    viewer: viewer === 'BOD' || viewer === 'CS-LEAD' ? viewer : 'ADMIN',
  };
}

function useWide(): boolean {
  const q = '(min-width: 1800px)';
  const [w, setW] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const f = () => setW(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return w;
}

export function ZaloOAPage() {
  const { state, money, viewer } = useMemo(readQuery, []);
  const toast = useToast();
  const wide = useWide();
  const [tab, setTab] = useState<Tab>('tpl');
  const [tf, setTf] = useState<'all' | TplStatus>('all');
  const [sel, setSel] = useState<string | null>(null);
  const [lf, setLf] = useState<'all' | LogType>('all');
  const [sf, setSf] = useState<LogStatus | null>(null);
  const [dockOpt, setDockOpt] = useState<boolean | null>(null);
  const [md, setMd] = useState<{ mode: 'new' | 'edit'; id?: string; name: string; ev: string; body: string; btn: string; tried: boolean } | null>(null);
  const [tpls, setTpls] = useState<Tpl[]>(TPLS);
  const [retried, setRetried] = useState<number[]>([]);

  const canCost = viewer === 'ADMIN' || viewer === 'BOD';
  const ro = state === 'noperm' || viewer === 'BOD';
  const dock = (dockOpt ?? wide) && state !== 'noperm';
  const list = tpls.filter((x) => tf === 'all' || x.st === tf);
  const cur = tpls.find((x) => x.id === sel) ?? list[0] ?? tpls[0]!;
  const logs = LOG.map((r) => (retried.includes(r.id) ? { ...r, st: 'sent' as LogStatus, why: '' } : r)).filter((r) => (lf === 'all' || r.ty === lf) && (!sf || r.st === sf));
  const ok = tpls.filter((x) => x.st === 'ok');
  const sentM = ok.reduce((a, x) => a + x.sent, 0);
  const errM = ok.reduce((a, x) => a + x.err, 0);
  const rcvM = sentM ? Math.round(((sentM - errM) / sentM) * 1000) / 10 : 0;
  const slaN = SLA_PIC.reduce((a, x) => a + x[1], 0);
  const slaOver = SLA_PIC.reduce((a, x) => a + x[2], 0);
  const slaOk = Math.round(((slaN - slaOver) / slaN) * 1000) / 10;

  const openNew = (init?: Partial<NonNullable<typeof md>>) =>
    !ro && setMd({ mode: 'new', name: '', ev: 'Giao hàng xong', body: BODY_DELIVERED, btn: 'Xem chi tiết lô', tried: false, ...init });
  const submit = () => {
    if (!md) return;
    if (!md.name.trim()) return setMd({ ...md, tried: true });
    if (md.mode === 'edit' && md.id) {
      setTpls((t) => t.map((x) => (x.id === md.id ? { ...x, st: 'wait', ver: 'v2', subAt: '22/Oct/2026 09:14', reason: '' } : x)));
      setSel(md.id);
      toast({ msg: `Đã gửi Zalo duyệt lại ${md.id} · v2` });
    } else {
      const id = `ZNS-N${tpls.length - TPLS.length + 1}`;
      setTpls((t) => [...t, { id, zid: '—', k: md.name.trim(), ev: md.ev, st: 'wait', ver: 'v1', p: ['job_id'], btn: md.btn || 'Xem chi tiết lô', sent: 0, rcv: 0, err: 0, subAt: '22/Oct/2026 09:14' }]);
      setSel(id);
      toast({ msg: `Đã gửi Zalo duyệt ${md.name.trim()}` });
    }
    setTf('all');
    setMd(null);
  };

  const kpis: [string, string, string, typeof IconSend, string, string, string][] = [
    ['ZNS tháng 10', fmt(sentM), 'tin', IconFileText, 'var(--pri-bg)', 'var(--link)', 'ZNS đã gửi từ 01/Oct'],
    ['Tỉ lệ nhận', pct(rcvM), '', IconCheck, 'var(--ok-bg)', 'var(--ok)', 'Tin tới máy người nhận / tin gửi'],
    ['Lỗi', fmt(errM), 'tin', IconAlertTriangle, 'var(--err-bg)', 'var(--err)', 'Xem lý do ở tab Thống kê'],
    ['Follower', fmt(FOLLOWERS), '+38', IconUsers, 'var(--sub)', 'var(--tx2)', '+38 follower trong tháng 10'],
    ['Broadcast còn', String(40 - BC_USED), '/ 40', IconSpeakerphone, 'var(--warn-bg)', 'var(--warn)', 'Tối đa 40 tin / 30 ngày cho follower'],
  ];
  if (canCost) kpis.push(money ? ['Chi phí ZNS', fmt(sentM * 250), '₫', IconCoin, 'var(--sub)', 'var(--tx2)', 'Chỉ ADMIN / BOD · ≈ 250 ₫ / tin'] : ['Lượt gửi ZNS', fmt(sentM), 'lượt', IconCoin, 'var(--sub)', 'var(--tx2)', 'Số tin ZNS đã gửi tháng 10 · số tiền chỉ hiện khi xem tiền']);

  const oa = state === 'error' ? { k: 'Lỗi đọc dữ liệu', dot: 'var(--err)', tone: 'err' as const } : state === 'empty' ? { k: 'Chưa kết nối', dot: 'var(--lb)', tone: 'neutral' as const } : { k: 'Đã kết nối', dot: 'var(--ok)', tone: 'ok' as const };

  return (
    <>
      <PageHeader
        title="Zalo OA"
        lead={
          <Link to="/kit" className={s.back} aria-label="Quay lại Quản trị › Tích hợp" title="Quản trị › Tích hợp">
            <IconArrowLeft size={17} stroke={2.2} aria-hidden="true" />
          </Link>
        }
        meta={
          <>
            <Chip tone={oa.tone} dot={oa.dot} label="Token tự gia hạn · lần gần nhất 22/Oct/2026 06:00">{oa.k}</Chip>
            <a className={s.hei} href="/hei/#kh" target="_blank" rel="noopener" title="Hội thoại khách (Zalo OA, Zalo cá nhân) xử lý ở HANA Hei › Khách">
              <IconMessageCircle size={14} stroke={1.9} aria-hidden="true" />
              {t('zalo.hei')} ↗
            </a>
          </>
        }
        sub={
          <span className={s.verified} title="ZNS, tư vấn, broadcast">
            HANA Logistics
            <IconRosetteDiscountCheck size={14} stroke={1.8} aria-hidden="true" style={{ color: 'var(--ok)' }} />
            {t('zalo.verified')}
          </span>
        }
        tabs={
          state === 'ready' || state === 'loading' ? (
            <Segmented<Tab> label="Zalo OA" value={tab} onChange={setTab} items={[{ key: 'tpl', label: t('zalo.znsTpl'), n: tpls.length }, { key: 'log', label: 'Nhật ký', n: LOG.length }, { key: 'stat', label: 'Thống kê' }]} />
          ) : undefined
        }
        actions={
          <>
            <Button aria-label="Gửi thử" icon={<IconSend size={18} aria-hidden="true" />} disabled={ro || cur.st !== 'ok'} title="Gửi mẫu đang chọn tới máy của bạn" onClick={() => toast({ msg: `Đã gửi thử ${cur.id} tới 09x xxx 123 · máy của bạn` })} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={ro} title={ro ? t('zalo.readOnly') : t('zalo.composeHint')} onClick={() => openNew()}>
              {t('zalo.znsTpl')}
            </Button>
            {!wide && <Button variant={dock ? 'soft' : 'secondary'} aria-label="Tình trạng kết nối OA" aria-pressed={dock} icon={<IconPlugConnected size={18} aria-hidden="true" />} onClick={() => setDockOpt(!dock)} />}
          </>
        }
      />
      <Page kind="table" dock={dock ? <Dock follow={fmt(FOLLOWERS)} err={state === 'error'} onClose={() => setDockOpt(false)} /> : undefined}>
        <StateView
          state={state === 'loading' ? 'ready' : state}
          title={state === 'empty' ? 'Chưa kết nối Zalo OA' : state === 'noperm' ? 'Không có quyền xem Zalo OA.' : 'Không đọc được dữ liệu từ Zalo.'}
          desc={state === 'error' ? 'Lần đọc cuối 22/Oct/2026 08:58.' : undefined}
          action={
            state === 'error' ? <Button icon={<IconRefresh size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Đang đọc lại dữ liệu từ Zalo…' })}>{t('kit.x.thuLai')}</Button>
            : state === 'empty' ? <Button variant="primary" icon={<IconPlugConnected size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Chưa mở kết nối ở bản này' })}>{t('kit.x.ketNoiO')}</Button>
            : undefined
          }
        >
          <div className={s.kpis} style={{ gridTemplateColumns: `repeat(${kpis.length}, minmax(0, 1fr))` }}>
            {kpis.map(([k, v, u, Ico, bg, fg, tip]) => (
              <div key={k} className={s.kpi} title={tip}>
                <span className={s.kpiIc} aria-hidden="true" style={{ background: bg, color: fg }}><Ico size={18} stroke={1.9} /></span>
                <div>
                  <div className={s.kpiK}>{k}</div>
                  <div className={s.kpiV}>{v}<span className={s.kpiU}>{u}</span></div>
                </div>
              </div>
            ))}
          </div>

          {tab === 'tpl' && (
            <div className={s.split}>
              <section aria-label="Thư viện mẫu ZNS" className={s.card}>
                <div className={s.bar}>
                  <Segmented<'all' | TplStatus>
                    label="Trạng thái duyệt"
                    value={tf}
                    onChange={setTf}
                    items={[{ key: 'all', label: 'Tất cả', n: tpls.length }, ...(['ok', 'wait', 'rej'] as const).map((k) => ({ key: k, label: ST[k].k, n: tpls.filter((x) => x.st === k).length, dot: ST[k].dot }))]}
                  />
                  <span className={s.info} title="Zalo duyệt mẫu trong 1–2 ngày làm việc. Chỉ mẫu Đã duyệt mới gửi được. Tên gửi ra ngoài: HANA Logistics.">i</span>
                </div>
                <table className={s.tbl}>
                  <thead>
                    <tr>
                      <th style={{ width: '34%' }}>{t('kit.x.mau')}</th><th style={{ width: '20%' }}>{t('kit.x.mocJob')}</th><th style={{ width: '15%' }}>{t('kit.x.duyet')}</th>
                      <th style={{ width: '9%' }} className={s.r}>{t('kit.x.gui30n')}</th><th style={{ width: '14%' }}>{t('kit.x.nhan')}</th><th style={{ width: '8%' }} className={s.r}>{t('kit.x.loi')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state === 'loading'
                      ? [1, 2, 3, 4, 5].map((i) => <tr key={i}><td colSpan={6}><div className={s.skel} /></td></tr>)
                      : list.map((x) => {
                          const on = x.id === cur.id;
                          return (
                            <tr key={x.id} aria-selected={on} className={on ? s.rowOn : undefined} onClick={() => setSel(x.id)}>
                              <td className={s.ell}><b>{x.k}</b><span className={s.code}>{x.id}</span></td>
                              <td className={`${s.ell} ${s.t2}`}>{x.ev}</td>
                              <td>
                                <Chip tone={ST[x.st].tone} dot="currentColor" label={x.st === 'rej' ? `Lý do: ${x.reason}` : x.st === 'wait' ? `Gửi duyệt ${x.subAt}` : `Template ID ${x.zid} · ${x.ver}`}>{ST[x.st].k}</Chip>
                              </td>
                              <td className={`${s.r} ${s.b}`}>{x.st === 'ok' ? fmt(x.sent) : '—'}</td>
                              <td>
                                <div className={s.rcv} title={x.st === 'ok' ? `Nhận ${pct(x.rcv)} · ${fmt(x.sent - x.err)} / ${fmt(x.sent)}` : 'Chưa gửi'}>
                                  <span className={s.rcvBar}><span style={{ width: `${x.st === 'ok' ? x.rcv : 0}%` }} /></span>
                                  <span>{x.st === 'ok' ? pct(x.rcv) : '—'}</span>
                                </div>
                              </td>
                              <td className={`${s.r} ${s.b}`} style={{ color: x.err ? 'var(--err)' : 'var(--lb)' }}>{x.st === 'ok' ? x.err : '—'}</td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
                {state !== 'loading' && !list.length && <div className={s.none}>{t('kit.x.khongCoMau')}</div>}
              </section>

              <section aria-label="Xem trước mẫu" className={s.preview}>
                <div className={s.phone}>
                  <div className={s.phHead}>
                    <span className={s.phLogo} aria-hidden="true">H</span>
                    <b>HANA Logistics</b>
                    <span className={s.phTick} title={t('zalo.verified')}>✓</span>
                  </div>
                  <div className={s.phBody}>
                    <div className={s.msg}>
                      <div className={s.msgHead}><span className={s.msgLogo} aria-hidden="true">H</span><span>HANA LOGISTICS</span></div>
                      <div className={s.msgTitle}>{cur.k}</div>
                      {cur.p.map((p) => <div key={p} className={s.msgRow}><span>{PARAMS[p][0]}</span><b>{PARAMS[p][2]}</b></div>)}
                      <div className={s.msgBtn}><span>{cur.btn}</span></div>
                    </div>
                    <div className={s.phNote}>{t('kit.x.tinDichVu')}</div>
                  </div>
                </div>
                <div className={s.pvSide}>
                  <div><b className={s.pvT}>{cur.k}</b><div className={s.pvMeta}>{cur.id} · {cur.zid !== '—' ? `ID ${cur.zid} · ` : ''}{cur.ver} · mốc {cur.ev}</div></div>
                  {cur.st === 'rej' && <div role="alert" className={`${s.note} ${s.noteErr}`}><b>{t('kit.x.zaloTuChoi')} </b>{cur.reason}</div>}
                  {cur.st === 'wait' && <div role="status" className={`${s.note} ${s.noteWarn}`}><b>{t('kit.x.choZaloDuyet')} </b>gửi {cur.subAt}</div>}
                  <div className={s.cap}>THAM SỐ · {cur.p.length}</div>
                  <div className={s.params}>
                    {cur.p.map((p) => (
                      <div key={p} className={s.param} title={`${PARAMS[p][0]} · mẫu ${PARAMS[p][2]}`}>
                        <code>{`<${p}>`}</code><span>{PARAMS[p][1]}</span><em>{p === 'amount' ? 'tiền' : /etd|eta|due|_at/.test(p) ? 'ngày' : 'chữ'}</em>
                      </div>
                    ))}
                  </div>
                  <div className={s.pvAct}>
                    {cur.st === 'rej' && <Button variant="primary" icon={<IconPencil size={16} aria-hidden="true" />} disabled={ro} onClick={() => !ro && setMd({ mode: 'edit', id: cur.id, name: cur.k, ev: cur.ev, body: BODY_DEBIT, btn: cur.btn, tried: false })}>{t('kit.x.suaVaGui')}</Button>}
                    <Button aria-label="Nhân bản" icon={<IconCopy size={16} aria-hidden="true" />} disabled={ro} onClick={() => openNew({ name: `${cur.k} (bản sao)`, ev: cur.ev, body: '', btn: cur.btn })} />
                    <Button variant="ghost" icon={<IconHistory size={16} aria-hidden="true" />} onClick={() => { setTab('log'); setLf('zns'); }}>{t('kit.x.xemNhatKy')}</Button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {tab === 'log' && (
            <section aria-label="Nhật ký gửi" className={s.card}>
              <div className={s.bar}>
                <Segmented<'all' | LogType> label="Loại tin" value={lf} onChange={setLf} items={[{ key: 'all', label: 'Tất cả', n: LOG.length }, ...(['chat', 'zns', 'bc'] as const).map((k) => ({ key: k, label: TY[k].k, n: LOG.filter((r) => r.ty === k).length }))]} />
                {(['seen', 'rcv', 'sent', 'err'] as const).map((k) => (
                  <button key={k} type="button" className={`${s.pill} ${sf === k ? s.pillOn : ''}`} aria-pressed={sf === k} title={SS[k].k} onClick={() => setSf(sf === k ? null : k)}>
                    <span className={s.dot} style={{ background: SS[k].dot }} />{SS[k].k}<span className={s.pn}>{LOG.filter((r) => r.st === k && (lf === 'all' || r.ty === lf)).length}</span>
                  </button>
                ))}
                <span className={s.grow} />
                <span className={s.foot}>{logs.length} tin · tên gửi HANA Logistics</span>
              </div>
              <table className={s.tbl}>
                <thead>
                  <tr>
                    <th style={{ width: 118 }}>{t('kit.x.gio')}</th><th style={{ width: 96 }}>{t('kit.x.loai')}</th><th style={{ width: '24%' }}>{t('kit.x.nguoiNhan')}</th><th>{t('kit.x.noiDungMau')}</th>
                    <th style={{ width: 116 }}>Job</th><th style={{ width: 96 }}>{t('kit.x.nguoiGui')}</th><th style={{ width: 150 }}>{t('kit.x.trangThai')}</th>
                  </tr>
                </thead>
                <tbody>
                  {state === 'loading'
                    ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => <tr key={i}><td colSpan={7}><div className={s.skel} /></td></tr>)
                    : logs.map((r) => {
                        const ty = TY[r.ty];
                        return (
                          <tr key={r.id}>
                            <td className={s.t2}>{r.at}</td>
                            <td><span className={s.ty} title={ty.tip} style={{ color: ty.color }}><ty.icon size={14} stroke={1.9} aria-hidden="true" />{ty.k}</span></td>
                            <td className={s.ell} title={`${r.who} · ${r.ph}`}><b>{r.who}</b><span className={s.code}>{r.ph}</span></td>
                            <td className={`${s.ell} ${s.t2}`} title={r.txt}>{r.txt}</td>
                            <td>{r.job && <a className={s.job} href="/ezy/jobs">{r.job}</a>}</td>
                            <td className={`${s.t2} ${s.sm}`}>{r.by}</td>
                            <td>
                              <span className={s.st}>
                                <span className={s.stK} title={r.st === 'err' ? `Lỗi: ${r.why}` : SS[r.st].tip} style={{ color: r.st === 'err' ? 'var(--err)' : 'var(--tx2)' }}><span className={s.dot8} style={{ background: SS[r.st].dot }} />{SS[r.st].k}</span>
                                {r.st === 'err' && r.ty === 'zns' && (
                                  <Button size="sm" icon={<IconRefresh size={14} aria-hidden="true" />} disabled={ro} onClick={() => { if (ro) return; setRetried((a) => [...a, r.id]); toast({ msg: `Đã gửi lại ${r.txt.split(' · ')[0]} tới ${r.who}` }); }}>{t('kit.x.guiLai')}</Button>
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
              {state !== 'loading' && !logs.length && <div className={s.none}>{t('kit.x.khongCoTin')}</div>}
            </section>
          )}

          {tab === 'stat' && <Stats sentM={sentM} slaOk={slaOk} slaN={slaN} slaOver={slaOver} ok={ok} canCost={canCost} money={money} />}
        </StateView>
      </Page>

      <Modal
        open={!!md}
        onOpenChange={(o) => !o && setMd(null)}
        title={md?.mode === 'edit' ? `Sửa mẫu · ${md.id}` : t('zalo.znsNew')}
        description="Gửi Zalo duyệt · 1–2 ngày làm việc"
        footer={<><Button variant="ghost" onClick={() => setMd(null)}>{t('kit.x.huy')}</Button><Button variant="primary" onClick={submit}>{t('kit.x.guiZaloDuyet')}</Button></>}
      >
        {md && (
          <div className={s.form}>
            <Input label="Tên mẫu" required value={md.name} error={md.tried && !md.name.trim() ? 'Nhập tên mẫu' : undefined} onChange={(e) => setMd({ ...md, name: e.target.value })} />
            <Select label="Mốc job" required value={md.ev} options={EVS} onChange={(e) => setMd({ ...md, ev: e.target.value })} />
            <div className={s.full}><Textarea label="Nội dung · tham số dạng <job_id>" rows={4} value={md.body} onChange={(e) => setMd({ ...md, body: e.target.value })} /></div>
            <div className={s.full}><Input label="Nút bấm" value={md.btn} onChange={(e) => setMd({ ...md, btn: e.target.value })} /></div>
          </div>
        )}
      </Modal>
    </>
  );
}

function Dock({ follow, err, onClose }: { follow: string; err: boolean; onClose: () => void }) {
  return (
    <div className={s.dock} role="complementary" aria-label="Tình trạng kết nối OA">
      <div className={s.dockHead}><b>{t('kit.x.ketNoiOa')}</b><button type="button" aria-label="Đóng bảng" className={s.x} onClick={onClose}><IconX size={17} aria-hidden="true" /></button></div>
      <div className={s.oaCard}>
        <span className={s.oaLogo} aria-hidden="true">H</span>
        <div><b>HANA Logistics</b><div className={s.pvMeta}>OA ID 43•••••18 · {follow} follower</div></div>
      </div>
      {TOKENS.map((k, i) => {
        const bad = err && i === 0;
        const c = bad ? 'var(--err)' : k.tone === 'ok' ? 'var(--ok)' : 'var(--warn)';
        return (
          <div key={k.k} className={s.tok} title={k.tip}>
            <div><span className={s.t2}>{k.k}</span><b style={{ color: c }}>{bad ? 'Lỗi' : k.v}</b></div>
            <span className={s.tokBar}><span style={{ width: k.w, background: c }} /></span>
          </div>
        );
      })}
      <div className={s.cap}>WEBHOOK</div>
      {HOOKS.map(([k, v, tip]) => (
        <div key={k} className={s.hook} title={tip}><span className={s.dot8} style={{ background: err ? 'var(--err)' : 'var(--ok)' }} /><span className={s.grow}>{k}</span><span className={s.foot}>{v}</span></div>
      ))}
      <a className={s.cfg} href="/kit"><IconExternalLink size={16} aria-hidden="true" />{t('kit.x.caiDatKet')}</a>
    </div>
  );
}

function Stats({ sentM, slaOk, slaN, slaOver, ok, canCost, money }: { sentM: number; slaOk: number; slaN: number; slaOver: number; ok: Tpl[]; canCost: boolean; money: boolean }) {
  const tiles: [string, string, string, string, string][] = [
    ['Ngưỡng', `${ZSLA} phút`, 'giờ làm việc', 'var(--tx)', 'Riêng Zalo · thư hộp chung 30 phút · ADMIN đổi ở Quản trị › Zalo OA'],
    ['Đúng SLA', pct(slaOk), `${fmt(slaN - slaOver)} / ${fmt(slaN)}`, slaOk >= 90 ? 'var(--ok)' : 'var(--warn)', `Hội thoại được trả lời trong ${ZSLA} phút giờ làm việc · tháng 10`],
    ['Trả lời trung vị', mmss(MEDIAN_SEC), 'tháng 10', 'var(--tx)', 'Từ tin khách tới tin trả lời đầu tiên · trừ ngoài giờ'],
    ['Quá SLA', fmt(slaOver), 'hội thoại', 'var(--err)', `Khách chờ quá ${ZSLA} phút giờ làm việc · tháng 10`],
    ['Đang quá', '2', 'ngay lúc này', 'var(--err)', 'Minh Quân (quá 12 phút) · Anh Phúc (quá 3 phút) · mở ở HANA Box › Quá SLA'],
  ];
  return (
    <>
      <section aria-label="SLA trả lời Zalo" className={`${s.card} ${s.sla}`}>
        <div className={s.slaHead}>
          <b>{t('kit.x.slaTraLoi')}</b><span className={s.foot}>{t('kit.x.5PhutGio')}</span>
          <a className={s.slaLink} href="/kit" title="Quản trị › AI & tích hợp › Zalo OA › SLA & tự tạo lead · chỉ ADMIN"><IconExternalLink size={14} aria-hidden="true" />{t('kit.x.oiNguongO')}</a>
        </div>
        <div className={s.tiles}>
          {tiles.map(([k, v, sub, c, tip]) => <div key={k} className={s.tile} title={tip}><div className={s.kpiK}>{k}</div><div className={s.tileV} style={{ color: c }}>{v}</div><div className={s.t2 + ' ' + s.sm}>{sub}</div></div>)}
        </div>
        <div role="table" aria-label="SLA theo người phụ trách">
          <div role="row" className={`${s.prow} ${s.phead}`}><span role="columnheader">PIC</span><span role="columnheader">{t('kit.x.ungSla')}</span><span role="columnheader" className={s.r}>{t('kit.x.tiLe')}</span><span role="columnheader" className={s.r}>{t('kit.x.hoiThoai')}</span><span role="columnheader" className={s.r}>{t('kit.x.qua')}</span><span role="columnheader" className={s.r}>{t('kit.x.trungVi')}</span></div>
          {SLA_PIC.map(([k, n, o, md]) => {
            const r = Math.round(((n - o) / n) * 1000) / 10;
            return (
              <div key={k} role="row" className={s.prow} title={`${k} · ${fmt(n)} hội thoại · quá SLA ${o} · trung vị ${mmss(md)}`}>
                <span role="cell" className={s.b}>{k}</span>
                <span role="cell" className={s.picBar}><span style={{ width: `${r}%`, background: r >= 90 ? 'var(--d-green)' : 'var(--d-amber)' }} /></span>
                <b role="cell" className={s.r}>{pct(r)}</b><span role="cell" className={`${s.r} ${s.t2}`}>{fmt(n)}</span>
                <b role="cell" className={s.r} style={{ color: o ? 'var(--err)' : 'var(--lb)' }}>{o}</b><span role="cell" className={`${s.r} ${s.t2}`}>{mmss(md)}</span>
              </div>
            );
          })}
        </div>
      </section>
      <div className={s.statGrid}>
        <section aria-label="ZNS theo ngày" className={s.panel}>
          <div className={s.slaHead}><b>{t('kit.x.zns14Ngay')}</b><span className={s.leg}><span><i style={{ background: 'var(--d-green)' }} />{t('kit.x.nhan')}</span><span><i style={{ background: 'var(--err)' }} />{t('kit.x.loi')}</span></span></div>
          <div role="img" aria-label="Biểu đồ số ZNS gửi mỗi ngày, 14 ngày gần nhất" className={s.chart}>
            {DAYS.map(([d, n, e]) => (
              <div key={d} title={`${d}/Oct · ${n} tin · lỗi ${e}`}>
                <span style={{ height: `${Math.round((e / DAY_MAX) * 100)}%`, background: 'var(--err)', borderRadius: '3px 3px 0 0' }} />
                <span style={{ height: `${Math.round(((n - e) / DAY_MAX) * 100)}%`, background: 'var(--d-green)', borderRadius: e ? 0 : '3px 3px 0 0' }} />
              </div>
            ))}
          </div>
          <div className={s.axis}>{DAYS.map(([d]) => <span key={d}>{d}</span>)}</div>
        </section>
        <section aria-label="Theo mốc job" className={`${s.panel} ${s.col}`}>
          <b>{t('kit.x.theoMocJob')}</b>
          {EVS.map((ev) => {
            const x = ok.find((y) => y.ev === ev);
            const n = x?.sent ?? 0;
            return (
              <div key={ev} className={s.evRow} title={x ? `${x.id} · nhận ${pct(x.rcv)}` : 'Mẫu chưa được duyệt'}>
                <span className={`${s.t2} ${s.ell}`}>{ev}</span><span className={s.evBar}><span style={{ width: `${Math.round((n / 412) * 100)}%` }} /></span><b className={s.r}>{n ? fmt(n) : '—'}</b>
              </div>
            );
          })}
        </section>
        <section aria-label="Lý do lỗi" className={`${s.panel} ${s.col}`}>
          <b>{t('kit.x.loiGuiThang')}</b>
          {ERRS.map(([k, n, tip]) => <div key={k} className={s.errRow} title={tip}><span className={s.dot8} style={{ background: 'var(--err)' }} /><span className={s.grow}>{k}</span><b>{n}</b></div>)}
        </section>
        <div className={s.pair}>
          <section aria-label="Broadcast" className={`${s.panel} ${s.col} ${s.mid}`}>
            <b className={s.self}>{t('kit.x.broadcast30Ngay')}</b>
            <div role="img" aria-label={`Đã dùng ${BC_USED} / 40 broadcast trong 30 ngày`} className={s.ring} style={{ background: `conic-gradient(var(--pri) ${Math.round((BC_USED / 40) * 360)}deg, var(--line) 0)` }}>
              <div><b>{40 - BC_USED}</b><span>{t('kit.x.con40')}</span></div>
            </div>
            <span className={s.t2 + ' ' + s.sm} title="Cửa sổ trượt 30 ngày · tin cũ nhất hết tính ngày 28/Oct">{t('kit.x.1LuotNgay')}</span>
          </section>
          <section aria-label="Chi phí ZNS" className={`${s.panel} ${s.col}`}>
            <b>{money ? 'Chi phí ZNS · tháng 10' : 'Lượt gửi ZNS · tháng 10'}</b>
            {canCost ? (
              <>
                <div className={s.big}>{money ? `${fmt(sentM * 250)} ₫` : `${fmt(sentM)} lượt`}</div>
                <div className={s.foot} title="Đơn giá ZNS theo gói OA · minh hoạ">{money ? `${fmt(sentM)} tin × 250 ₫` : 'tin ZNS đã gửi'}</div>
                {money && <a className={s.slaLink} href="/kit" style={{ marginTop: 'auto' }}><IconExternalLink size={14} aria-hidden="true" />{t('kit.x.chiPhiHe')}</a>}
              </>
            ) : (
              <div className={s.noCost}><IconLock size={16} aria-hidden="true" />{t('kit.x.chiAdminBod')}</div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
